import { forwardRef, type ElementType, type HTMLAttributes } from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/cn";

const textVariants = cva("bfrs-tracking-normal", {
  variants: {
    variant: {
      display: "bfrs-text-5xl bfrs-font-bold bfrs-leading-[1.1] sm:bfrs-text-6xl",
      h1: "bfrs-text-4xl bfrs-font-bold bfrs-leading-tight",
      h2: "bfrs-text-3xl bfrs-font-semibold bfrs-leading-tight",
      h3: "bfrs-text-2xl bfrs-font-semibold bfrs-leading-snug",
      title: "bfrs-text-xl bfrs-font-semibold bfrs-leading-snug",
      body: "bfrs-text-base bfrs-font-normal bfrs-leading-7",
      "body-sm": "bfrs-text-sm bfrs-font-normal bfrs-leading-[1.45]",
      "body-xs": "bfrs-text-[12px] bfrs-font-normal bfrs-leading-[1.4]",
      caption: "bfrs-text-xs bfrs-font-normal bfrs-leading-normal",
      label: "bfrs-text-[10px] bfrs-font-bold bfrs-uppercase bfrs-leading-normal bfrs-tracking-[0.16em]"
    },
    tone: {
      primary: "bfrs-text-text-primary",
      secondary: "bfrs-text-text-secondary",
      muted: "bfrs-text-text-muted",
      disabled: "bfrs-text-text-disabled",
      danger: "bfrs-text-danger",
      success: "bfrs-text-success",
      accent: "bfrs-text-accent"
    },
    // Optional override for each variant's default weight — e.g. make a small
    // caption bold, or a large heading normal. Applied after the variant class,
    // so it wins the font-weight conflict via tailwind-merge in cn().
    weight: {
      normal: "bfrs-font-normal",
      medium: "bfrs-font-medium",
      semibold: "bfrs-font-semibold",
      bold: "bfrs-font-bold"
    }
  },
  defaultVariants: {
    variant: "body",
    tone: "primary"
  }
});

export type TextProps = HTMLAttributes<HTMLElement> &
  VariantProps<typeof textVariants> & {
    as?: ElementType;
  };

const defaultTagByVariant: Record<NonNullable<TextProps["variant"]>, ElementType> = {
  display: "h1",
  h1: "h1",
  h2: "h2",
  h3: "h3",
  title: "p",
  body: "p",
  "body-sm": "p",
  "body-xs": "p",
  caption: "p",
  label: "span"
};

export const Text = forwardRef<HTMLElement, TextProps>(({ as, className, variant, tone, weight, ...props }, ref) => {
  const resolvedVariant = variant ?? "body";
  const Component = as ?? defaultTagByVariant[resolvedVariant];
  return <Component ref={ref} className={cn(textVariants({ variant: resolvedVariant, tone, weight }), className)} {...props} />;
});

Text.displayName = "Text";
